'use client'

import React from 'react'

import { ChevronRight } from 'lucide-react'
import Link from 'next/link'
import { twMerge } from 'tailwind-merge'

// ============================================================================
// Types
// ============================================================================

export interface BreadcrumbItem {
  label: string
  href?: string
  current?: boolean
  /** If true, renders as a code-style badge (for IDs, numbers) */
  isCode?: boolean
}

interface BreadcrumbsV2Props {
  items: BreadcrumbItem[]
  className?: string
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * BreadcrumbsV2 - Navigation breadcrumb trail
 *
 * Displays hierarchical navigation with optional links and code-style badges
 * for identifiers.
 *
 * @example
 * ```tsx
 * <BreadcrumbsV2
 *   items={[
 *     { label: 'SBIR/STTR', href: '/sbirs/search' },
 *     { label: 'SOCOM254-008', current: true, isCode: true },
 *   ]}
 * />
 * ```
 */
const BreadcrumbsV2: React.FC<BreadcrumbsV2Props> = ({ items, className }) => {
  return (
    <nav
      aria-label="Breadcrumb"
      className={twMerge(
        'flex items-center gap-2 text-sm font-medium text-primary-500 dark:text-primary-400',
        className,
      )}
    >
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {/* Separator */}
          {index > 0 && (
            <ChevronRight className="h-3 w-3 text-primary-400 dark:text-primary-600" />
          )}

          {/* Breadcrumb Item */}
          {item.isCode ? (
            // Code-style badge for IDs/numbers
            <span className="rounded border border-primary-200 bg-primary-100 px-2 py-0.5 font-mono text-xs text-primary-900 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-100">
              {item.label}
            </span>
          ) : item.current ? (
            // Current page (no link)
            <span
              className="text-primary-900 dark:text-primary-100"
              aria-current="page"
            >
              {item.label}
            </span>
          ) : item.href ? (
            // Link to other page
            <Link
              href={item.href}
              className="cursor-pointer transition-colors hover:text-primary-900 dark:hover:text-primary-100"
            >
              {item.label}
            </Link>
          ) : (
            // Non-link item
            <span className="cursor-pointer transition-colors hover:text-primary-900 dark:hover:text-primary-100">
              {item.label}
            </span>
          )}
        </React.Fragment>
      ))}
    </nav>
  )
}

BreadcrumbsV2.displayName = 'BreadcrumbsV2'

export { BreadcrumbsV2 }
export type { BreadcrumbsV2Props }
